import React, { useState } from 'react';
import styled from 'styled-components';

const StyledErrorID = styled.p`
  font-size: ${p => p.theme.fontSize.sm};
  color: ${p => p.theme.colors.danger};
`;

type ErrorMessage = {
  error: { error_message: string; error_id: string } | string;
  status?: number;
};

export type AlertsState = {
  message: string | React.ReactNode;
  type: string;
  staticAlert?: boolean;
  dismissable?: boolean;
  setMessage(
    message: ErrorMessage,
    type: string,
    staticAlert?: boolean,
    dismissable?: boolean
  ): void;
  clearMessage(): void;
};

const AlertsContext = React.createContext<AlertsState>({
  message: undefined,
  type: '',
  staticAlert: false,
  dismissable: true,
  setMessage: () => null,
  clearMessage: () => null,
});

const provideAlerts = (WrappedComponent: any) => {
  const ProvideAlerts = (props: any) => {
    const [message, setMessage] = useState<string | React.ReactNode>();
    const [type, setType] = useState<string>('');
    const [staticAlert, setStaticAlert] = useState<boolean | undefined>(false);
    const [dismissable, setDismissable] = useState<boolean | undefined>(true);

    const createAlert = ({ error, status }: ErrorMessage) => {
      return typeof error === 'string' ? (
        <div>
          {status && `${status} - `}
          {error}
        </div>
      ) : (
        <div>
          <p>
            {status} - {error.error_message}
          </p>
          {error.error_id && (
            <StyledErrorID>
              Error ID: <code>{error.error_id}</code>
            </StyledErrorID>
          )}
        </div>
      );
    };

    const setAlert = (
      alertMessage: ErrorMessage,
      alertType: string,
      isStatic?: boolean,
      isDismissable?: boolean
    ) => {
      setMessage(createAlert(alertMessage));
      setType(alertType);
      setStaticAlert(isStatic);
      setDismissable(undefined || isDismissable ? true : false);
    };

    const clearAlert = () => {
      setMessage(undefined);
      setType('');
      setStaticAlert(false);
      setDismissable(true);
    };

    return (
      <AlertsContext.Provider
        value={{
          message: message,
          type: type,
          staticAlert: staticAlert,
          dismissable: dismissable,
          setMessage: setAlert,
          clearMessage: clearAlert,
        }}
      >
        <WrappedComponent {...props} />
      </AlertsContext.Provider>
    );
  };
  return ProvideAlerts;
};

// eslint-disable-next-line react/display-name
const withAlerts = (WrappedComponent: any) => (props: any) => (
  <AlertsContext.Consumer>
    {(v: AlertsState) => <WrappedComponent {...props} alerts={v} />}
  </AlertsContext.Consumer>
);

export default AlertsContext;
export { provideAlerts, withAlerts };
